'use client';

import { Button, DatePicker } from '@heroui/react';
import { parseDate } from '@internationalized/date';
import { FaCircleXmark } from 'react-icons/fa6';

interface ClosingDateFieldProps {
  /** `YYYY-MM-DD`, or `''` for a job with no closing date. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  description?: string;
  isDisabled?: boolean;
  className?: string;
}

/**
 * The optional closing date, wherever a job is booked or edited.
 *
 * One component for all three surfaces — the internal scheduling form, the
 * online scheduler and the work order — so the value they produce is the same
 * shape everywhere. `DateValue.toString()` on a day-granular picker is already
 * `YYYY-MM-DD`, which is exactly what the API stores and what Attik hands
 * EverHome, so nothing here formats or reparses a date.
 *
 * Empty is a real answer, not a missing one: the field never blocks a booking,
 * and clearing it is how a job that fell out of contract gets corrected.
 */
export default function ClosingDateField({
  value,
  onChange,
  label = 'Closing Date',
  description = 'Optional — the day the sale closes',
  isDisabled,
  className,
}: ClosingDateFieldProps) {
  // A stored value that will not parse (a hand-edited record, an import) shows
  // as empty rather than throwing the form away.
  let parsed = null;
  try {
    parsed = value ? parseDate(value) : null;
  } catch {
    parsed = null;
  }

  // The hint sits outside the picker, as a plain line like the other scheduling
  // fields' help text, rather than in HeroUI's padded description slot.
  return (
    <div className={`space-y-1 ${className ?? ''}`}>
      <div className='relative'>
        <DatePicker
          label={label}
          granularity='day'
          showMonthAndYearPickers
          size='sm'
          variant='bordered'
          className='w-full'
          classNames={{
            inputWrapper: 'border bg-white shadow-none',
            // Inputs and Selects shrink a filled inside label to 85%; the picker's
            // label never does, so match that here or it reads a size bigger.
            label: 'font-light uppercase text-xs origin-top-left scale-85',
          }}
          value={parsed}
          onChange={(date) => onChange(date ? date.toString() : '')}
          isDisabled={isDisabled}
        />
        {/*
          HeroUI's DatePicker has no isClearable, and its end slot belongs to the
          calendar button. So the clear sits over the field, just left of it.
        */}
        {value && !isDisabled && (
          <Button
            isIconOnly
            size='sm'
            variant='light'
            radius='full'
            aria-label={`Clear ${label.toLowerCase()}`}
            onPress={() => onChange('')}
            className='absolute right-9 top-1/2 z-10 h-6 w-6 min-w-6 -translate-y-1/2 text-default-400'
          >
            <FaCircleXmark size={14} />
          </Button>
        )}
      </div>
      {description && <p className='text-xs text-gray-500'>{description}</p>}
    </div>
  );
}
